import type {
  IqSpecFields,
  ApprovalStage,
  FormListStats,
  AmendInput,
  ApiConnection,
  ApprovalTiming,
  DecisionRights,
  StageApprovers,
  ReminderSettings,
  AuditListResponse,
  EmailTemplateVersion,
  EmailTemplateView,
  Approval,
  CreateFolderInput,
  CreateFormInput,
  CompanyProfile,
  CreateUserInput,
  DecisionInput,
  FieldDef,
  FieldOption,
  Form,
  FormDetail,
  FormVersionDetail,
  FormFolder,
  IqDerivation,
  LookupDef,
  MeResponse,
  PortalUser,
  PublicFormsResponse,
  Submission,
  SubmissionListItem,
  SubmissionStatus,
  TeamsBotStatus,
  UpdateFolderInput,
  UpdateFormInput,
  UpdateUserInput,
} from '@jfh/intake-shared';
import type { CompanyLookupOutcome } from '@jfh/intake-forms/renderer';
import { postcodeOutcome } from '@jfh/intake-forms/renderer';
import { STAGING_FORMS_BASE_URL } from './deployment.js';
import { apiFetch } from './teams.js';

/** Inbox grouping passed to the submissions list endpoint. */
export type SubmissionGroup = 'customers' | 'suppliers' | 'leads';

/** A raw audit row as returned by D1 (snake_case, JSON strings or null). */
export interface ActivityRow {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  actor: string;
  before: string | null;
  after: string | null;
  metadata: string | null;
  created_at: string;
}

export interface IqPostLog {
  action: string;
  status: number | null;
  ok: boolean;
  iqStatus: string | null;
  code: string | null;
  message: string | null;
  stubbed: boolean;
  createdAt: string;
}

export interface SubmissionDetailResponse {
  submission: Submission;
  form: Form | null;
  schema: FieldDef[];
  approvals: Approval[];
  activity: ActivityRow[];
  iqPosts: IqPostLog[];
  timing: ApprovalTiming | null;
  /** Whether the signed-in person may decide the current stage. */
  decision?: DecisionRights | null;
  /** The approval card should be in Teams but failed to post (the error). */
  teamsCardError?: string | null;
  /** F10: the emails approving and declining send now, and whether each is on. */
  emails?: { approve: DecisionEmail; decline: DecisionEmail } | null;
  /** F10: required staff questions still empty (approving is refused until filled). */
  missingForApproval?: string[];
}

export interface DecisionEmail {
  key: string;
  name: string;
  on: boolean;
}

/** The current stage's Teams card, as approvers see it (F10). */
export interface CardPreview {
  channel: string;
  stage: ApprovalStage;
  body: unknown[];
}

/** An API error, with the details the API sent (e.g. `{ missing: [...] }`). */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown,
  ) {
    super(message);
  }
}


export type Me = MeResponse;

interface ApiError {
  error: string;
  details?: unknown;
}

async function request<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, ...rest } = init ?? {};
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set('content-type', 'application/json');
  const res = await apiFetch(path, {
    ...rest,
    headers,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    let details: unknown;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) message = body.error;
      details = body?.details;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiRequestError(message, res.status, details);
  }
  return (await res.json()) as T;
}

/** Save a file the API returns (e.g. a CSV export) under its suggested name. */
async function download(path: string): Promise<{ truncatedAt: number | null }> {
  const res = await apiFetch(path);
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as ApiError;
      if (body?.error) message = body.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'export.csv';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  const cap = res.headers.get('x-audit-truncated');
  return { truncatedAt: cap ? Number(cap) : null };
}

export const api = {
  me: () => request<Me>('/api/admin/me'),

  listForms: (includeArchived = false) =>
    request<{ forms: Form[]; stats?: Record<string, FormListStats> }>(
      includeArchived ? '/api/admin/forms?archived=1' : '/api/admin/forms',
    ),

  createForm: (input: CreateFormInput) =>
    request<{ form: FormDetail }>('/api/admin/forms', {
      method: 'POST',
      json: input,
    }),

  getForm: (id: string) =>
    request<{ form: FormDetail }>(`/api/admin/forms/${id}`),

  updateForm: (id: string, input: UpdateFormInput) =>
    request<{ form: FormDetail }>(`/api/admin/forms/${id}`, {
      method: 'PUT',
      json: input,
    }),

  // One published version with its questions (History: show / restore).
  getFormVersion: (id: string, versionId: string) =>
    request<{ version: FormVersionDetail }>(
      `/api/admin/forms/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`,
    ),

  publishForm: (id: string) =>
    request<{ form: FormDetail }>(`/api/admin/forms/${id}/publish`, {
      method: 'POST',
    }),

  archiveForm: (id: string) =>
    request<{ form: FormDetail }>(`/api/admin/forms/${id}/archive`, {
      method: 'POST',
    }),

  unarchiveForm: (id: string) =>
    request<{ form: FormDetail }>(`/api/admin/forms/${id}/unarchive`, {
      method: 'POST',
    }),

  cloneForm: (id: string) =>
    request<{ form: FormDetail }>(`/api/admin/forms/${id}/clone`, {
      method: 'POST',
    }),

  deleteForm: (id: string) =>
    request<{ ok: true }>(`/api/admin/forms/${id}`, { method: 'DELETE' }),

  /** Move a form to a folder (null = top level). */
  moveForm: (id: string, folderId: string | null) =>
    request<{ form: FormDetail }>(`/api/admin/forms/${id}`, {
      method: 'PUT',
      json: { folderId } satisfies UpdateFormInput,
    }),

  listFolders: () => request<{ folders: FormFolder[] }>('/api/admin/folders'),

  createFolder: (input: CreateFolderInput) =>
    request<{ folder: FormFolder }>('/api/admin/folders', {
      method: 'POST',
      json: input,
    }),

  updateFolder: (id: string, input: UpdateFolderInput) =>
    request<{ folder: FormFolder }>(`/api/admin/folders/${id}`, {
      method: 'PUT',
      json: input,
    }),

  deleteFolder: (id: string) =>
    request<{ ok: true }>(`/api/admin/folders/${id}`, { method: 'DELETE' }),

  getLookup: (key: string) =>
    request<{ options: FieldOption[] }>(`/api/admin/lookups/${key}/resolve`),

  listLookups: () => request<{ lookups: LookupDef[] }>('/api/admin/lookups'),
  /** The builder's iQ field list, read live from the API's spec (null = use the built-in one). */
  getIqFields: () =>
    request<{ fields: IqSpecFields | null; source: 'live' | 'saved' | 'none'; readAt: string | null }>('/api/admin/iq-fields'),

  /**
   * The public form's Companies House lookup, through this origin's /api proxy
   * (the builder's Try it out). Never throws: maps failures as the form does.
   */
  /** Postcode look-up for staff editing an address, through the admin's /api proxy. Never throws. */
  lookupPostcode: (postcode: string) =>
    postcodeOutcome(() =>
      apiFetch(`/api/public/postcodes/${encodeURIComponent(postcode)}`, { headers: { accept: 'application/json' } }),
    ),

  lookupCompany: async (number: string): Promise<CompanyLookupOutcome> => {
    try {
      const res = await apiFetch(`/api/public/companies/${encodeURIComponent(number)}`, {
        headers: { accept: 'application/json' },
      });
      if (res.status === 404 || res.status === 400) return { kind: 'not_found' };
      if (res.status === 429) return { kind: 'busy' };
      if (!res.ok) return { kind: 'unavailable' };
      const body = (await res.json()) as { company: CompanyProfile };
      return { kind: 'found', company: body.company };
    } catch {
      return { kind: 'unavailable' };
    }
  },

  /** A few raw records from an API list, to pick its columns (F9 P5). */
  sampleLookup: (def: LookupDef) =>
    request<{ resultsPath: string | null; records: unknown[]; error: string | null }>('/api/admin/lookups/sample', {
      method: 'POST',
      json: def,
    }),

  previewLookup: (def: LookupDef) =>
    request<{ options: FieldOption[] }>('/api/admin/lookups/preview', {
      method: 'POST',
      json: def,
    }),

  putLookup: (key: string, def: LookupDef) =>
    request<{ ok: true; lookup: LookupDef }>(`/api/admin/lookups/${key}`, {
      method: 'PUT',
      json: def,
    }),

  deleteLookup: (key: string) =>
    request<{ ok: true }>(`/api/admin/lookups/${key}`, { method: 'DELETE' }),

  getWixWebhook: () =>
    request<{ secret: string; url: string }>('/api/admin/wix-webhook'),

  putWixWebhook: (secret: string) =>
    request<{ ok: true }>('/api/admin/wix-webhook', { method: 'PUT', json: { secret } }),

  getApiConnection: () =>
    request<ApiConnection>('/api/admin/api-connection'),

  putApiConnection: (body: {
    baseUrl: string;
    bearerToken?: string;
    cfAccessClientId?: string;
    cfAccessClientSecret?: string;
    customerEndpoint?: string;
    supplierEndpoint?: string;
    leadEndpoint?: string;
  }) =>
    request<{ ok: true }>('/api/admin/api-connection', {
      method: 'PUT',
      json: body,
    }),

  listUsers: () => request<{ users: PortalUser[] }>('/api/admin/users'),

  createUser: (input: CreateUserInput) =>
    request<{ user: PortalUser }>('/api/admin/users', {
      method: 'POST',
      json: input,
    }),

  updateUser: (id: string, input: UpdateUserInput) =>
    request<{ user: PortalUser }>(`/api/admin/users/${id}`, {
      method: 'PUT',
      json: input,
    }),

  deleteUser: (id: string) =>
    request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),

  /** Audit log (admins). `params`: from, to, actor, cat, q, entity, cursor. */
  listAudit: (params: URLSearchParams) =>
    request<AuditListResponse>(`/api/admin/audit?${params.toString()}`),

  auditActors: () => request<{ actors: { actor: string; count: number }[] }>('/api/admin/audit/actors'),

  exportAuditCsv: (params: URLSearchParams) => download(`/api/admin/audit/export.csv?${params.toString()}`),

  listEmailTemplates: () => request<{ templates: EmailTemplateView[] }>('/api/admin/email-templates'),

  saveEmailTemplate: (key: string, input: { subject: string; source: string; enabled: boolean }) =>
    request<{ template: EmailTemplateView }>(`/api/admin/email-templates/${key}`, { method: 'PUT', json: input }),

  resetEmailTemplate: (key: string) =>
    request<{ template: EmailTemplateView }>(`/api/admin/email-templates/${key}/reset`, { method: 'POST' }),

  previewEmailTemplate: (key: string, input: { subject: string; source: string }) =>
    request<{ subject: string; html: string }>(`/api/admin/email-templates/${key}/preview`, { method: 'POST', json: input }),

  testEmailTemplate: (key: string, input: { subject: string; source: string }) =>
    request<{ status: string; to: string }>(`/api/admin/email-templates/${key}/test`, { method: 'POST', json: input }),

  emailTemplateVersions: (key: string) =>
    request<{ versions: EmailTemplateVersion[] }>(`/api/admin/email-templates/${key}/versions`),

  restoreEmailTemplate: (key: string, versionId: string) =>
    request<{ template: EmailTemplateView }>(`/api/admin/email-templates/${key}/versions/${versionId}/restore`, { method: 'POST' }),

  getSettings: () =>
    request<{ settings: Record<string, unknown> }>('/api/admin/settings'),

  // A staging build ignores the saved setting: its links must stay on staging.
  getFormsBaseUrl: () =>
    STAGING_FORMS_BASE_URL
      ? Promise.resolve({ url: STAGING_FORMS_BASE_URL })
      : request<{ url: string }>('/api/admin/forms-base-url'),

  listPublicForms: async () => {
    const res = await request<PublicFormsResponse>('/api/admin/public-forms');
    return STAGING_FORMS_BASE_URL ? { ...res, baseUrl: STAGING_FORMS_BASE_URL } : res;
  },

  getApprovers: () => request<{ approvers: StageApprovers }>('/api/admin/approvers'),
  putApprovers: (approvers: Partial<StageApprovers>) =>
    request<{ approvers: StageApprovers }>('/api/admin/approvers', { method: 'PUT', body: JSON.stringify({ approvers }) }),

  getReminders: () => request<{ settings: ReminderSettings }>('/api/admin/reminders'),
  putReminders: (settings: Partial<ReminderSettings>) =>
    request<{ settings: ReminderSettings }>('/api/admin/reminders', { method: 'PUT', body: JSON.stringify({ settings }) }),

  getIqDerivations: () =>
    request<{ derivations: IqDerivation[] }>('/api/admin/iq-derivations'),

  putSetting: (key: string, value: unknown) =>
    request<{ ok: true }>(`/api/admin/settings/${key}`, {
      method: 'PUT',
      json: { value },
    }),

  testTeams: (channel: string) =>
    request<{
      configured: boolean;
      delivered: boolean;
      status: number | null;
      message?: string;
    }>('/api/admin/teams/test', { method: 'POST', json: { channel } }),

  /** JFH Forms Teams app: set-up state, teams and channels. */
  getTeamsBot: () => request<TeamsBotStatus>('/api/admin/teams/bot'),

  /** Post a test card through the Teams app to a channel's chosen target. */
  testTeamsBot: (channel: string) =>
    request<{ delivered: boolean; message?: string }>('/api/admin/teams/bot/test', {
      method: 'POST',
      json: { channel },
    }),

  listSubmissions: (group: SubmissionGroup, status?: SubmissionStatus) => {
    const params = new URLSearchParams({ group });
    if (status) params.set('status', status);
    return request<{ submissions: SubmissionListItem[] }>(
      `/api/admin/submissions?${params.toString()}`,
    );
  },

  getSubmission: (id: string) =>
    request<SubmissionDetailResponse>(`/api/admin/submissions/${id}`),

  decideSubmission: (id: string, input: DecisionInput) =>
    request<{ submission: Submission }>(`/api/admin/submissions/${id}/decision`, {
      method: 'POST',
      json: input,
    }),

  amendSubmission: (id: string, input: AmendInput) =>
    request<{ submission: Submission }>(`/api/admin/submissions/${id}/amend`, {
      method: 'POST',
      json: input,
    }),

  getCardPreview: (id: string) => request<CardPreview>(`/api/admin/submissions/${id}/card`),

  resendCard: (id: string) =>
    request<{ posted: boolean; channel?: string; stage?: string }>(
      `/api/admin/submissions/${id}/resend-card`,
      { method: 'POST' },
    ),

  resendIq: (id: string) =>
    request<{
      ok: boolean;
      entity: 'lead' | 'customer';
      op: 'create' | 'update';
      recordId: string | null;
      code: string | null;
      status: number | null;
      iqStatus: string | null;
      message: string | null;
      stubbed: boolean;
    }>(`/api/admin/submissions/${id}/resend-iq`, { method: 'POST' }),

  needsFixing: () =>
    request<{ submissions: SubmissionListItem[] }>('/api/admin/needs-fixing'),

  getIqAlertEmails: () =>
    request<{ emails: string[] }>('/api/admin/iq-alert-emails'),

  getEmailConnection: () =>
    request<{ apiKeySet: boolean; from: string }>('/api/admin/email-connection'),

  putEmailConnection: (body: { from?: string; resendApiKey?: string }) =>
    request<{ ok: true }>('/api/admin/email-connection', { method: 'PUT', json: body }),

  testEmail: (to: string) =>
    request<{ configured: boolean; status: string; message?: string }>(
      '/api/admin/email/test',
      { method: 'POST', json: { to } },
    ),

  testIq: (body: { method?: string; entity?: 'lead' | 'customer' }) =>
    request<{
      configured: boolean;
      url: string;
      method: string;
      ok: boolean;
      status: number | null;
      gotHeaders: boolean;
      headers: Record<string, string | null>;
      body: string | null;
      error: string | null;
    }>('/api/admin/iq/test', { method: 'POST', json: body }),

  getCountryConfig: () =>
    request<{ disabled: string[]; default: string }>('/api/admin/country-config'),

  putCountryConfig: (body: { disabled?: string[]; default?: string }) =>
    request<{ ok: true }>('/api/admin/country-config', { method: 'PUT', json: body }),
};
